/**
 * Barre de filtres e3 « façon On » (MarketplaceBarLayout) : téléphone (e3) et ordinateur (e3-wide).
 * Une seule logique ; seule la mise en page change (CSS `_bar-e3.scss`).
 *
 * Le HTML vient du serveur (`partials/marketplace/action-bar-e3`) : champ de recherche (téléphone),
 * « Filtrer » et pastilles de marques (ou de modèles sur la landing d'une marque), de vrais liens vers
 * les landings ; sur ordinateur, une flèche fait défiler la rangée et « Trier » est au bout de la barre.
 * Ici, on les enrichit à partir de l'état et des facettes d'InstantSearch, sans nouvelle requête :
 * - un appui sur une pastille raffine sur place (le lien reste pour l'ouverture dans un nouvel onglet) ;
 * - les choix s'empilent en tête avec une croix (« Porsche ✕ » « 911 ✕ » « Essence ✕ ») ;
 * - puis le niveau suivant, avec ses compteurs : modèles de la marque, puis carburants de la sélection,
 *   puis des raccourcis vers la feuille Filtrer (Mensualité, Kilométrage, Prix) ;
 * - jamais de pastille à 0 véhicule.
 * La feuille « Trier par » (bouton « Trier » du titre, ou de la barre sur ordinateur, où elle s'ouvre en
 * menu sous le bouton) pilote le même tri que la barre.
 */

import { SHEET_EXIT_MS, bindSheetDrag, prefersReducedMotion } from "../shared/sheet-motion";
import { familiesOf, familyToggleValues, groupModelFacetCounts } from "./model-families";

export type ChipKind = "make" | "model" | "energy" | "opener";

export interface E3Chip {
    kind: ChipKind;
    value: string;
    label: string;
    count?: number;
    active?: boolean;
    href?: string | null;
}

export interface FacetCount {
    value: string;
    count: number;
}

export interface E3ChipState {
    /**
     * Marque imposée par la page (landing d'une marque ou d'un modèle, page dynamique filtrée) : pastille
     * active en tête, avec sa croix (la retirer renvoie vers la marketplace sans marque).
     */
    pageMake: string | null;
    /**
     * Marques imposées par une page dynamique qui en impose plusieurs : une pastille active chacune (un
     * appui ne retire qu'elle), puis les autres marques. Vide sinon.
     */
    pageMakes?: string[];
    make: string | null;
    models: string[];
    energies: string[];
    /** Marques du HTML serveur (avec leur lien), compteurs du stock. */
    ssrMakes: E3Chip[];
    /** Modèles du HTML serveur (landing d'une marque), avec leur lien s'il existe. */
    ssrModels: E3Chip[];
    /** Compteurs en direct (dernière réponse d'InstantSearch) ; null avant la première. */
    makeCounts: FacetCount[] | null;
    modelCounts: FacetCount[] | null;
    energyCounts: FacetCount[] | null;
    energyLabels: Record<string, string>;
}

/** Raccourcis vers les sections de la feuille Filtrer, une fois les valeurs épuisées. */
export const E3_OPENERS: { value: string; label: string }[] = [
    { value: "monthly_price", label: "Mensualité" },
    { value: "mileage", label: "Kilométrage" },
    { value: "price", label: "Prix" },
];

/** Chemin de la landing d'une marque, comme les slugs du catalogue (« Rolls-Royce » → rolls-royce). */
export function makeLandingPath(make: string): string {
    const slug = make
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return `/location-longue-duree-${slug}`;
}

function byCountDesc(a: FacetCount, b: FacetCount): number {
    return b.count - a.count || a.value.localeCompare(b.value, "fr");
}

/** Pastilles de la rangée, dans l'ordre : choix actifs, puis le niveau suivant (jamais à 0). */
export function computeE3Chips(state: E3ChipState): E3Chip[] {
    const chips: E3Chip[] = [];
    const effectiveMake = state.pageMake ?? state.make;
    const energyLabel = (value: string): string => state.energyLabels[value] ?? value;
    const pageMakes = state.pageMakes ?? [];

    if (!state.pageMake && pageMakes.length > 1) {
        // Même rangée que le serveur (VehicleMake::listingChipsForPage) : marques de la page, puis les autres
        pageMakes.forEach((make) => chips.push({ kind: "make", value: make, label: make, active: true, href: null }));
        const live = state.makeCounts ? new Map(state.makeCounts.map((f) => [f.value, f.count])) : null;
        state.ssrMakes
            .filter((chip) => !pageMakes.includes(chip.value))
            .map((chip) => ({ ...chip, count: live ? (live.get(chip.value) ?? chip.count ?? 0) : (chip.count ?? 0) }))
            .filter((chip) => chip.count > 0)
            .sort((a, b) => byCountDesc({ value: a.label, count: a.count }, { value: b.label, count: b.count }))
            .forEach((chip) => chips.push({ ...chip, active: false }));

        return chips;
    }

    if (state.pageMake) {
        // Même pastille que le serveur (VehicleMake::listingChipsForPage) : bouton, sans lien
        chips.push({ kind: "make", value: state.pageMake, label: state.pageMake, active: true, href: null });
    } else if (state.make) {
        const ssr = state.ssrMakes.find((chip) => chip.value === state.make);
        chips.push({ kind: "make", value: state.make, label: ssr?.label ?? state.make, active: true, href: ssr?.href ?? makeLandingPath(state.make) });
    }
    state.models.forEach((model) => {
        const ssr = state.ssrModels.find((chip) => chip.value === model);
        chips.push({ kind: "model", value: model, label: model, active: true, href: ssr?.href ?? null });
    });
    state.energies.forEach((energy) => {
        chips.push({ kind: "energy", value: energy, label: energyLabel(energy), active: true });
    });

    if (!effectiveMake) {
        // Comptage du serveur en échec (liste vide) : les marques viennent des facettes de la recherche
        if (state.ssrMakes.length === 0) {
            (state.makeCounts ?? [])
                .filter((facet) => facet.count > 0)
                .sort(byCountDesc)
                .forEach((facet) => chips.push({
                    kind: "make",
                    value: facet.value,
                    label: facet.value,
                    count: facet.count,
                    href: makeLandingPath(facet.value),
                }));

            return chips;
        }

        const live = state.makeCounts ? new Map(state.makeCounts.map((f) => [f.value, f.count])) : null;
        state.ssrMakes
            .map((chip) => ({ ...chip, count: live ? (live.get(chip.value) ?? 0) : (chip.count ?? 0) }))
            .filter((chip) => chip.count > 0)
            .sort((a, b) => byCountDesc({ value: a.label, count: a.count }, { value: b.label, count: b.count }))
            .forEach((chip) => chips.push({ ...chip, active: false }));

        return chips;
    }

    if (state.models.length === 0) {
        const counts =
            state.modelCounts ??
            state.ssrModels.map((chip) => ({ value: chip.value, count: chip.count ?? 0 }));
        counts
            .filter((facet) => facet.count > 0)
            .sort(byCountDesc)
            .forEach((facet) => {
                const ssr = state.ssrModels.find((chip) => chip.value === facet.value);
                chips.push({ kind: "model", value: facet.value, label: facet.value, count: facet.count, href: ssr?.href ?? null });
            });

        return chips;
    }

    const energies = state.energies.length === 0
        ? (state.energyCounts ?? []).filter((facet) => facet.count > 0).sort(byCountDesc)
        : [];
    energies.forEach((facet) => {
        chips.push({ kind: "energy", value: facet.value, label: energyLabel(facet.value), count: facet.count });
    });

    if (energies.length === 0 && state.energyCounts !== null) {
        E3_OPENERS.forEach((opener) => chips.push({ kind: "opener", ...opener }));
    }

    return chips;
}

const ICON_REMOVE =
    '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8"/></svg>';
const ICON_DOWN =
    '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 4l4 4 4-4"/></svg>';

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** HTML d'une pastille : lien quand la valeur a une landing, bouton sinon. */
export function renderE3Chip(chip: E3Chip): string {
    const classes = [
        "mkt-e3-bar__chip",
        chip.active ? "mkt-e3-bar__chip--active" : "",
        chip.kind === "opener" ? "mkt-e3-bar__chip--opener" : "",
    ].filter(Boolean).join(" ");
    const attrs =
        `class="${classes}" data-attr="listing-chip" data-chip-kind="${chip.kind}" data-chip-value="${escapeHtml(chip.value)}"` +
        (chip.active ? ` aria-label="${escapeHtml(`Retirer ${chip.label}`)}"` : "");
    const inner =
        escapeHtml(chip.label) +
        (!chip.active && chip.count ? ` <span class="mkt-e3-bar__chip-count">${chip.count}</span>` : "") +
        (chip.active ? `<span class="mkt-e3-bar__chip-remove" aria-hidden="true">${ICON_REMOVE}</span>` : "") +
        (chip.kind === "opener" ? ICON_DOWN : "");

    return chip.href
        ? `<a href="${escapeHtml(chip.href)}" ${attrs}>${inner}</a>`
        : `<button type="button" ${attrs}>${inner}</button>`;
}

/** Libellé de la rangée pour les lecteurs d'écran, selon le niveau proposé. */
export function e3ChipsLabel(chips: E3Chip[]): string {
    const next = chips.find((chip) => !chip.active)?.kind;
    if (next === "make") return "Marques";
    if (next === "model") return "Modèles";
    if (next === "energy") return "Carburants";

    return "Filtres choisis";
}

// ── Branchement sur InstantSearch ─────────────────────────────────────────────

interface HelperLike {
    state: {
        hierarchicalFacetsRefinements?: Record<string, string[]>;
        facetsRefinements?: Record<string, string[]>;
        disjunctiveFacetsRefinements?: Record<string, string[]>;
    };
    setPage: (page: number) => HelperLike;
    toggleFacetRefinement: (attribute: string, value: string) => HelperLike;
    clearRefinements: (attribute: string) => HelperLike;
    search: () => HelperLike;
}

interface ResultsLike {
    getFacetValues: (attribute: string, options?: unknown) => unknown;
    /** Réponses brutes : la 1re est la requête principale, avec tous les filtres actifs */
    _rawResults?: Array<{ facets?: Record<string, Record<string, number>> }>;
}

interface SearchLike {
    on: (event: "render", cb: () => void) => void;
    mainIndex: {
        getHelper: () => unknown;
        getResults: () => unknown;
    };
}

interface E3Options {
    energyLabels: Record<string, string>;
    /** Facettes retirées avec la marque (`MAKE_DEPENDENT_REFINEMENT_FACETS`). */
    makeDependentFacets: readonly string[];
    /** Signale un choix du visiteur (sinon une landing remet ses filtres d'origine). */
    onUserFilterInteraction: () => void;
    /**
     * Page qui renvoie ses filtres vers /marketplace (landing, page dynamique) : un appui sur une pastille
     * part sur la marketplace avec cette valeur ajoutée (ou retirée, pour un modèle imposé par la page),
     * sans attendre que la page soit prête. Absent sur la marketplace, qui filtre sur place.
     */
    leaveForMarketplaceToggling?: (
        attribute: "make" | "model" | "energy_type",
        value: string,
        /** Marques imposées par la page (pastilles actives), quand l'état de la recherche ne les porte pas encore. */
        pageMakes?: string[],
    ) => void;
}

type RefinementState = HelperLike["state"];

/** Facettes qui décident du niveau de pastilles affiché. */
const CHIP_FACETS = ["make", "model", "energy_type"] as const;

function refinementsOf(helper: HelperLike | null, attribute: string): string[] {
    return refinementsOfState(helper?.state ?? null, attribute);
}

export function refinementsOfState(state: RefinementState | null | undefined, attribute: string): string[] {
    if (!state) return [];

    return [
        ...(state.hierarchicalFacetsRefinements?.[attribute] ?? []),
        ...(state.facetsRefinements?.[attribute] ?? []),
        ...(state.disjunctiveFacetsRefinements?.[attribute] ?? []),
    ].filter((value, index, all) => value !== "" && all.indexOf(value) === index);
}

/**
 * La dernière réponse porte-t-elle sur les filtres demandés ? Pendant une recherche lente, l'état
 * contient déjà « Porsche » alors que les facettes sont encore celles de la réponse précédente (toutes
 * marques) : on garderait sinon des modèles d'une autre marque (« M3 » sous « Porsche »).
 */
export function resultsMatchState(
    requested: RefinementState | null | undefined,
    answered: RefinementState | null | undefined,
): boolean {
    if (!requested || !answered) return false;

    return CHIP_FACETS.every((attribute) => {
        const a = [...refinementsOfState(requested, attribute)].sort();
        const b = [...refinementsOfState(answered, attribute)].sort();

        return a.length === b.length && a.every((value, index) => value === b[index]);
    });
}

/** Valeurs d'une facette de la dernière réponse (liste à plat, ou arbre d'une facette hiérarchique). */
export function facetCounts(results: ResultsLike | null, attribute: string): FacetCount[] | null {
    if (!results) return null;
    let values: unknown;
    try {
        values = results.getFacetValues(attribute, { sortBy: ["count:desc", "name:asc"] });
    } catch {
        return null;
    }
    const list = Array.isArray(values)
        ? values
        : values && typeof values === "object" && Array.isArray((values as { data?: unknown }).data)
          ? (values as { data: unknown[] }).data
          : null;
    if (!list) return null;

    return list
        .map((item) => item as { name?: string; escapedValue?: string; count?: number })
        .filter((item) => typeof item.name === "string")
        .map((item) => ({ value: item.name as string, count: Number(item.count ?? 0) }));
}

/**
 * Compteurs de la requête principale (tous les filtres actifs) : « Essence 5 » après Porsche puis Macan,
 * c'est-à-dire ce qu'on obtient en touchant la pastille. Les valeurs de `getFacetValues` d'une facette
 * disjonctive peuvent venir d'une requête qui ignore un autre filtre (« Essence 30 », toutes les
 * Porsche). Pour les modèles et les carburants, proposés seulement tant que leur facette n'est pas
 * filtrée, la requête principale est la bonne. Repli sur `facetCounts` sans réponse brute.
 */
export function selectionFacetCounts(results: ResultsLike | null, attribute: string): FacetCount[] | null {
    const facet = results?._rawResults?.[0]?.facets?.[attribute];
    if (!facet || typeof facet !== "object") return facetCounts(results, attribute);

    return Object.entries(facet)
        .map(([value, count]) => ({ value, count: Number(count ?? 0) }))
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/** `data-page-makes` (JSON) : marques imposées par une page dynamique qui en impose plusieurs. */
export function parsePageMakes(raw: string | undefined): string[] {
    if (!raw) return [];
    try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string" && v !== "") : [];
    } catch {
        return [];
    }
}

function readSsrChips(nav: HTMLElement, kind: "make" | "model"): E3Chip[] {
    return Array.from(nav.querySelectorAll<HTMLElement>(`[data-chip-kind="${kind}"]`)).map((el) => ({
        kind,
        value: el.dataset.chipValue ?? "",
        label: (el.firstChild?.textContent ?? el.dataset.chipValue ?? "").trim(),
        count: Number(el.querySelector(".mkt-e3-bar__chip-count")?.textContent ?? 0),
        href: el instanceof HTMLAnchorElement ? el.getAttribute("href") : null,
    }));
}

/**
 * Marques du stock rendues par le serveur : la rangée elle-même, ou, quand une marque est déjà choisie dans
 * l'URL (la rangée montre alors cette marque et ses modèles), la liste complète gardée dans `data-ssr-makes`.
 */
function readSsrMakes(nav: HTMLElement): E3Chip[] {
    if (nav.dataset.ssrMakes) {
        try {
            return (JSON.parse(nav.dataset.ssrMakes) as Array<{ value: string; label: string; count: number | null; href: string | null }>)
                .map((chip) => ({ kind: "make" as const, value: chip.value, label: chip.label, count: chip.count ?? 0, href: chip.href }));
        } catch {
            return [];
        }
    }
    return readSsrChips(nav, "make");
}

/**
 * Où démarre la rangée de pastilles (px depuis son origine) : au début, sauf si la dernière pastille
 * active (avec sa ✕) passe sous le fondu de 36 px (3 pastilles actives à 320 px) : la rangée part alors
 * de la première pastille qui la laisse entière (point d'accroche du défilement, scroll-snap).
 * Priorité à gauche : jamais au-delà de la première pastille active, qui reste entière, juste à droite
 * du bouton Filtrer, sans passer dessous.
 */
export function chipsRevealStart(starts: number[], firstActiveStart: number, lastActiveRight: number, room: number, leftFade = 0): number {
    if (lastActiveRight <= room) return 0;
    // Ordinateur : un fondu couvre le bord gauche d'une rangée défilée, la première active reste en dehors
    const limit = firstActiveStart - leftFade;
    let fallback = 0;
    for (const start of starts) {
        if (start > limit) break;
        if (lastActiveRight - start <= room) return start;
        fallback = start;
    }

    return Math.max(0, fallback);
}

function revealLastActiveChip(nav: HTMLElement): void {
    nav.scrollLeft = 0;
    const actives = nav.querySelectorAll<HTMLElement>(".mkt-e3-bar__chip--active");
    const first = actives[0];
    const last = actives[actives.length - 1];
    if (!first || !last) return;
    const padding = parseFloat(getComputedStyle(nav).paddingLeft || "0");
    const origin = nav.getBoundingClientRect().left + padding;
    const starts = Array.from(nav.querySelectorAll<HTMLElement>(".mkt-e3-bar__chip")).map((chip) => chip.getBoundingClientRect().left - origin);
    nav.scrollLeft = chipsRevealStart(starts, first.getBoundingClientRect().left - origin, last.getBoundingClientRect().right - origin, nav.clientWidth - padding - 36, nav.closest(".mkt-e3-bar--wide") ? 48 : 0);
}

function updateScrollFade(nav: HTMLElement): void {
    const atEnd = nav.scrollLeft + nav.clientWidth >= nav.scrollWidth - 1;
    nav.classList.toggle("is-scroll-end", atEnd);
    nav.classList.toggle("is-scroll-start", nav.scrollLeft <= 0);
}

/** État de la rangée de pastilles : tout tient (pas de flèche), ou bout atteint (la flèche revient). */
export function chipsScrollState(scrollLeft: number, clientWidth: number, scrollWidth: number): { fits: boolean; atEnd: boolean } {
    const fits = scrollWidth <= clientWidth + 1;

    return { fits, atEnd: fits || scrollLeft + clientWidth >= scrollWidth - 1 };
}

/** Prochaine position de la rangée pour la flèche : une page moins 96 px (le fondu), ou le début au bout. */
export function nextChipsScrollLeft(scrollLeft: number, clientWidth: number, scrollWidth: number): number {
    if (chipsScrollState(scrollLeft, clientWidth, scrollWidth).atEnd) return 0;

    return Math.min(scrollLeft + Math.max(clientWidth - 96, 120), scrollWidth - clientWidth);
}

/** Ordinateur : flèche « › » au bout de la rangée, masquée quand tout tient, « ‹ » une fois au bout. */
function initChipsMoreButton(nav: HTMLElement): (() => void) | null {
    const more = document.querySelector<HTMLButtonElement>("[data-mkt-e3-more]");
    if (!more) return null;

    const sync = (): void => {
        const { fits, atEnd } = chipsScrollState(nav.scrollLeft, nav.clientWidth, nav.scrollWidth);
        more.hidden = fits;
        more.classList.toggle("is-back", !fits && atEnd);
        more.setAttribute("aria-label", !fits && atEnd ? "Revenir au début" : "Voir la suite");
    };
    more.addEventListener("click", () => {
        nav.scrollTo({ left: nextChipsScrollLeft(nav.scrollLeft, nav.clientWidth, nav.scrollWidth), behavior: "smooth" });
    });
    nav.addEventListener("scroll", sync, { passive: true });
    if (typeof ResizeObserver !== "undefined") new ResizeObserver(sync).observe(nav);
    sync();

    return sync;
}

/** Ouvre la feuille Filtrer (le panneau existant) sur une section. */
function openFilterSheetAt(widget: string): void {
    document.querySelector<HTMLElement>('.mkt-e3-bar__filter[data-action="toggle-filter"]')?.click();
    window.setTimeout(() => {
        const panel = document.querySelector<HTMLElement>(`#sidebar [data-widget="${widget}"]`);
        panel?.scrollIntoView({ block: "start" });
        const header = panel?.querySelector<HTMLElement>('.widget-header[aria-expanded="false"]');
        header?.click();
    }, 320);
}

/**
 * Menu « Trier par » de l'ordinateur : 8 px sous le filet bas de la barre, bord droit sur celui du bouton.
 * Quand le menu ne tient pas dessous (barre d'un bloc d'annonces en bas de la fenêtre) et qu'il y a plus de
 * place au-dessus, il s'ouvre 8 px au-dessus de la barre. Toujours dans la fenêtre (16 px de marge) : au-delà,
 * il défile à l'intérieur (fenêtre basse, zoom).
 */
export function sortMenuGeometry(
    barTop: number,
    barBottom: number,
    triggerRight: number,
    viewportWidth: number,
    viewportHeight: number,
    menuHeight: number,
): { top: number; right: number; maxHeight: number; above: boolean } {
    const gap = 8;
    const margin = 16;
    const right = Math.round(viewportWidth - triggerRight);
    const below = Math.floor(viewportHeight - (barBottom + gap) - margin);
    const aboveSpace = Math.floor(barTop - gap - margin);

    if (menuHeight > below && aboveSpace > below) {
        const maxHeight = Math.max(0, Math.min(Math.ceil(menuHeight), aboveSpace));

        return { top: Math.round(barTop - gap - maxHeight), right, maxHeight, above: true };
    }

    return { top: Math.round(barBottom + gap), right, maxHeight: Math.max(0, below), above: false };
}

/** Événement de la feuille « Trier par » quand la page n'a pas de <select> de tri (marketplace-search.ts). */
export const E3_SORT_REFINE_EVENT = "mkt-e3-sort-refine";

/** `data-short-labels` de la feuille (JSON) : valeur du tri → libellé court. */
export function parseShortLabels(raw: string | undefined): Record<string, string> {
    if (!raw) return {};
    try {
        const parsed: unknown = JSON.parse(raw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
            ? Object.fromEntries(Object.entries(parsed).filter((e): e is [string, string] => typeof e[1] === "string"))
            : {};
    } catch {
        return {};
    }
}

/** Libellé du bouton sans tri choisi, et toujours au téléphone sur la liste de la marketplace. */
export const SORT_TRIGGER_DEFAULT = "Trier";

/** Texte du bouton « Trier » : le libellé court du tri actif, ou « Trier » pour le tri par défaut. */
export function sortTriggerText(shortLabel: string | undefined | null): { text: string; sorted: boolean } {
    const short = (shortLabel ?? "").trim();

    return short === "" ? { text: SORT_TRIGGER_DEFAULT, sorted: false } : { text: short, sorted: true };
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Garde le focus clavier dans une feuille ouverte : Tab et Maj+Tab bouclent sur ses éléments visibles.
 * Pour un groupe de radios, seul le radio coché (ou le premier) compte, comme le fait le navigateur.
 */
function trapFocus(container: HTMLElement, event: KeyboardEvent): void {
    if (event.key !== "Tab") return;
    const seenGroups = new Set<string>();
    const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
        if (el.getClientRects().length === 0) return false;
        if (el instanceof HTMLInputElement && el.type === "radio" && el.name) {
            const group = Array.from(container.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${el.name}"]`));
            const stop = group.find((radio) => radio.checked) ?? group[0];
            if (el !== stop || seenGroups.has(el.name)) return false;
            seenGroups.add(el.name);
        }
        return true;
    });
    if (items.length === 0) {
        event.preventDefault();
        return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (!active || !container.contains(active)) {
        event.preventDefault();
        first.focus();
    } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
    }
}

function initSortSheet(search: SearchLike, onUserFilterInteraction: () => void): void {
    const sheet = document.getElementById("mkt-e3-sort-sheet");
    // Plusieurs boutons possibles : liste de la marketplace au téléphone, « Trier » sur la ligne du titre et,
    // dans la ligne de la recherche, celui que montre une liste filtrée (`mp-filtered`) ; un seul est affiché
    const triggers = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-mkt-e3-sort-open]"));
    const trigger = triggers[0];
    if (!sheet || !trigger) return;
    // Bouton qui a ouvert la feuille : il reprend le focus à la fermeture
    let opener: HTMLButtonElement = trigger;
    // Hors du contenu de la page, pour pouvoir rendre le reste inerte pendant qu'elle est ouverte
    document.body.appendChild(sheet);

    const sortSelect = (): HTMLSelectElement | null =>
        document.querySelector<HTMLSelectElement>("[data-sort-mirror] .hits-sort-by__select-native") ??
        document.querySelector<HTMLSelectElement>(".marketplace-action-bar .hits-sort-by__select-native");
    const radios = (): HTMLInputElement[] =>
        Array.from(sheet.querySelectorAll<HTMLInputElement>('input[type="radio"]'));
    const background = (): HTMLElement[] =>
        Array.from(document.body.children).filter(
            (el): el is HTMLElement => el instanceof HTMLElement && el !== sheet && el.tagName !== "SCRIPT",
        );

    // Libellés courts de tous les tris (MarketplaceChromeHelper::sortShortLabels), anciens compris
    const shortLabels = parseShortLabels(sheet.dataset.shortLabels);
    // Tri en cours : le <select> relié s'il existe, sinon la valeur posée par le connecteur de tri
    const currentSort = (): string | null => sortSelect()?.value || sheet.dataset.currentSort || null;

    const sync = (): void => {
        const value = currentSort();
        if (!value) return;
        radios().forEach((radio) => {
            radio.checked = radio.value === value;
        });
        const current = radios().find((radio) => radio.checked);
        // Tri autre que « Pertinence » : son libellé court sur le bouton, état actif (rendu serveur identique) ;
        // un tri absent de la feuille (`?sortBy=power_desc`) garde le sien
        const short = sortTriggerText(current ? current.dataset.shortLabel : shortLabels[value]);
        const label = current?.closest("label")?.textContent?.trim() ?? (short.sorted ? short.text : null);
        triggers.forEach((button) => {
            button.setAttribute("aria-label", label && short.sorted ? `Trier, actuellement ${label}` : "Trier");
            // Téléphone (`data-mkt-e3-sort-fixed-label`) : toujours « Trier », le point jaune signale le tri choisi
            const buttonText = "mktE3SortFixedLabel" in button.dataset ? SORT_TRIGGER_DEFAULT : short.text;
            const text = button.querySelector<HTMLElement>("[data-mkt-e3-sort-label]");
            if (text && text.textContent !== buttonText) text.textContent = buttonText;
            button.classList.toggle("is-sorted", short.sorted);
        });
    };
    const apply = (radio: HTMLInputElement): void => {
        const select = sortSelect();
        if (select && select.value !== radio.value) {
            // Choix du visiteur : sinon une landing (mode marketplace compris) remet son état d'origine
            // quand le <select> relié est hors de la zone suivie (celui du titre)
            onUserFilterInteraction();
            select.value = radio.value;
            select.dispatchEvent(new Event("change", { bubbles: true }));
        } else if (!select && currentSort() !== radio.value) {
            // Page sans <select> de tri : la feuille passe directement par le connecteur de tri
            onUserFilterInteraction();
            document.dispatchEvent(new CustomEvent(E3_SORT_REFINE_EVENT, { detail: radio.value }));
        }
    };

    // Ordinateur (« Trier » dans la barre) : menu sous la barre, aligné sur le bord droit du bouton, sans
    // bloquer le défilement (pas de saut de la barre de défilement) : il suit la barre collante quand la
    // page défile (flèches du clavier sur Safari, molette) et se ferme si la fenêtre change de taille.
    // Au téléphone (bloc d'annonces d'une landing, « Trier » dans la rangée) : feuille du bas, comme la marketplace
    const asMenu = trigger.classList.contains("mkt-e3-sort--bar") && document.body.classList.contains("mkt-bar--e3-wide");
    const placeMenu = (): void => {
        const bar =
            opener.closest<HTMLElement>(".marketplace-global-chrome__action-bar, .marketplace-excerpt__action-bar") ?? opener;
        const barRect = bar.getBoundingClientRect();
        const { top, right, maxHeight } = sortMenuGeometry(
            barRect.top,
            barRect.bottom,
            opener.getBoundingClientRect().right,
            document.documentElement.clientWidth,
            window.innerHeight,
            panel?.scrollHeight ?? 0,
        );
        sheet.style.setProperty("--mkt-e3-sort-top", `${top}px`);
        sheet.style.setProperty("--mkt-e3-sort-right", `${right}px`);
        sheet.style.setProperty("--mkt-e3-sort-max-height", `${maxHeight}px`);
    };

    // Téléphone : la feuille se ferme en glissant (200 ms) avant d'être masquée ; rouverte entre-temps, elle
    // repart de sa position du moment (transitions CSS, `_bar-e3.scss`)
    const panel = sheet.querySelector<HTMLElement>(".mkt-e3-sort-sheet__panel");
    const grip = sheet.querySelector<HTMLElement>(".mkt-e3-sort-sheet__grip");
    let hideTimer: number | undefined;
    let resetDrag = (): void => {};
    const close = (): void => {
        if (sheet.hidden || !sheet.classList.contains("is-open")) return;
        sheet.classList.remove("is-open");
        // Glissé au doigt : la sortie part de la position du doigt (la transformation en ligne cède à la classe)
        if (panel) panel.style.transform = "";
        background().forEach((el) => el.removeAttribute("inert"));
        if (asMenu) {
            window.removeEventListener("scroll", placeMenu);
            window.removeEventListener("resize", close);
        } else {
            document.documentElement.style.removeProperty("overflow");
        }
        opener.focus({ preventScroll: true });
        window.clearTimeout(hideTimer);
        const hide = (): void => {
            if (sheet.classList.contains("is-open")) return;
            sheet.hidden = true;
            resetDrag();
        };
        if (asMenu) hide();
        else hideTimer = window.setTimeout(hide, prefersReducedMotion() ? 200 : SHEET_EXIT_MS);
    };
    const open = (): void => {
        if (sheet.classList.contains("is-open")) return;
        window.clearTimeout(hideTimer);
        sync();
        const wasHidden = sheet.hidden;
        sheet.hidden = false;
        // Après l'affichage (même image) : la hauteur du menu décide s'il s'ouvre dessous ou dessus
        if (asMenu) placeMenu();
        // Une image affichée fermée, puis ouverte : la transition part du bas (ou de là où elle en était)
        if (wasHidden) void sheet.offsetHeight;
        sheet.classList.add("is-open");
        background().forEach((el) => el.setAttribute("inert", ""));
        if (asMenu) {
            window.addEventListener("scroll", placeMenu, { passive: true });
            window.addEventListener("resize", close);
        } else {
            document.documentElement.style.overflow = "hidden";
        }
        (radios().find((radio) => radio.checked) ?? radios()[0])?.focus({ preventScroll: true });
    };

    triggers.forEach((button) =>
        button.addEventListener("click", () => {
            opener = button;
            open();
        }),
    );
    sheet.querySelectorAll("[data-mkt-e3-sort-close]").forEach((el) => el.addEventListener("click", close));
    // Feuille du bas : la poignée se tire vers le bas (comme les volets de la fiche)
    if (!asMenu && panel && grip) {
        resetDrag = bindSheetDrag({
            handle: grip,
            panel,
            enabled: () => sheet.classList.contains("is-open"),
            close,
        });
    }

    // Les flèches changent le tri sans fermer ; un appui (souris, doigt) ou Entrée / Espace ferment
    let pointerChoice = false;
    let pointerOnChecked = false;
    sheet.addEventListener("pointerdown", (event) => {
        const label = event.target instanceof Element ? event.target.closest("label") : null;
        pointerChoice = label !== null;
        pointerOnChecked = label?.querySelector<HTMLInputElement>('input[type="radio"]')?.checked ?? false;
    });
    // Appui sur le tri déjà choisi : pas de « change », on ferme quand même
    sheet.addEventListener("click", (event) => {
        if (!pointerOnChecked || !(event.target instanceof Element) || !event.target.closest("label")) return;
        pointerOnChecked = false;
        pointerChoice = false;
        window.setTimeout(close, 200);
    });
    sheet.addEventListener("change", (event) => {
        const radio = event.target;
        if (!(radio instanceof HTMLInputElement) || radio.type !== "radio") return;
        apply(radio);
        if (pointerChoice) {
            pointerChoice = false;
            window.setTimeout(close, 200);
        }
    });
    sheet.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            close();
            return;
        }
        trapFocus(sheet, event);
        if ((event.key === "Enter" || event.key === " ") && event.target instanceof HTMLInputElement && event.target.type === "radio") {
            event.preventDefault();
            event.target.checked = true;
            apply(event.target);
            close();
        }
    });
    search.on("render", sync);
}

/**
 * Feuille Filtrer (le panneau existant) en e3 : focus sur la croix à l'ouverture, Tab gardé dans le
 * panneau, Échap pour fermer, focus rendu au bouton Filtrer. e2 n'est pas concerné.
 */
function initFilterSheetKeyboard(): void {
    const sidebar = document.getElementById("sidebar");
    const opener = document.querySelector<HTMLElement>(".mkt-e3-bar__filter");
    if (!sidebar || !opener) return;
    const closeButton = (): HTMLElement | null => sidebar.querySelector<HTMLElement>("#sidebar-close");
    let wasOpen = sidebar.classList.contains("open");

    new MutationObserver(() => {
        const isOpen = sidebar.classList.contains("open");
        if (isOpen === wasOpen) return;
        wasOpen = isOpen;
        if (isOpen) {
            window.setTimeout(() => closeButton()?.focus({ preventScroll: true }), 50);
        } else if (sidebar.contains(document.activeElement) || document.activeElement === document.body) {
            opener.focus({ preventScroll: true });
        }
    }).observe(sidebar, { attributes: true, attributeFilter: ["class"] });

    document.addEventListener("keydown", (event) => {
        if (!sidebar.classList.contains("open")) return;
        if (event.key === "Escape") {
            event.preventDefault();
            closeButton()?.click();
            return;
        }
        trapFocus(sidebar, event);
    });
}

/** Branche la barre e3 si la page l'a rendue (sinon, rien). */
export function initMarketplaceBarE3(search: SearchLike, options: E3Options): void {
    const nav = document.querySelector<HTMLElement>("[data-mkt-e3-chips]");
    initSortSheet(search, options.onUserFilterInteraction);
    if (!nav) return;

    const pageMake = nav.dataset.pageMake || null;
    const pageMakes = parsePageMakes(nav.dataset.pageMakes);
    initFilterSheetKeyboard();
    const ssrMakes = pageMake ? [] : readSsrMakes(nav).filter((chip) => !pageMakes.includes(chip.value));
    const ssrModels = pageMake ? readSsrChips(nav, "model").filter((chip) => (chip.count ?? 0) > 0) : [];
    const helper = (): HelperLike | null => search.mainIndex.getHelper() as HelperLike | null;

    let signature = computeE3Chips({
        pageMake,
        pageMakes,
        make: null,
        models: [],
        energies: [],
        ssrMakes,
        ssrModels,
        makeCounts: null,
        modelCounts: null,
        energyCounts: null,
        energyLabels: options.energyLabels,
    }).map(renderE3Chip).join("");

    // Sous 360 px, le texte d'aide entier ne tient pas dans le champ (maquette : « Marque, modèle… »)
    const shortenPlaceholder = (): void => {
        const input = document.querySelector<HTMLInputElement>(".mkt-e3-bar__search .aa-Input");
        if (input && window.matchMedia("(max-width: 359.98px)").matches) input.placeholder = "Marque, modèle…";
    };
    shortenPlaceholder();
    search.on("render", shortenPlaceholder);

    nav.addEventListener("scroll", () => updateScrollFade(nav), { passive: true });
    updateScrollFade(nav);
    const syncMoreButton = initChipsMoreButton(nav);

    const render = (): void => {
        const h = helper();
        const results = search.mainIndex.getResults() as (ResultsLike & { _state?: RefinementState }) | null;
        // Réponse en retard sur l'état demandé : on garde les pastilles affichées jusqu'à la bonne
        if (!results || !resultsMatchState(h?.state, results._state)) return;
        const make = refinementsOf(h, "make")[0] ?? null;
        // Une pastille par famille de modèles (« Cayenne » pour Cayenne II et III choisis)
        const models = familiesOf(refinementsOf(h, "model"));
        const chips = computeE3Chips({
            pageMake,
            pageMakes,
            make: pageMake && make === pageMake ? null : make,
            models,
            energies: refinementsOf(h, "energy_type"),
            ssrMakes,
            ssrModels,
            makeCounts: facetCounts(results, "make"),
            modelCounts: groupModelFacetCounts(selectionFacetCounts(results, "model")),
            energyCounts: selectionFacetCounts(results, "energy_type"),
            energyLabels: options.energyLabels,
        });
        const html = chips.map(renderE3Chip).join("");
        if (html === signature) return;
        signature = html;
        nav.innerHTML = html;
        nav.setAttribute("aria-label", e3ChipsLabel(chips));
        revealLastActiveChip(nav);
        updateScrollFade(nav);
        syncMoreButton?.();
    };
    search.on("render", render);

    nav.addEventListener("click", (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const chip = target?.closest<HTMLElement>("[data-chip-kind]");
        if (!chip || !nav.contains(chip)) return;
        // Ouverture dans un nouvel onglet : le lien vers la landing reste un vrai lien
        if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) return;
        event.preventDefault();

        const kind = chip.dataset.chipKind as ChipKind;
        if (kind !== "opener") options.onUserFilterInteraction();
        const value = chip.dataset.chipValue ?? "";
        const active = chip.classList.contains("mkt-e3-bar__chip--active");
        const h = helper();

        if (kind === "opener") {
            openFilterSheetAt(value);
            return;
        }
        if (kind === "make") {
            if (active && options.leaveForMarketplaceToggling) {
                // Landing, page dynamique : retirer la marque mène à /marketplace sans elle, explicitement
                // (une landing sans stock n'a pas toujours la croix du filtre Marque)
                options.leaveForMarketplaceToggling("make", value, pageMakes.length > 0 ? pageMakes : pageMake ? [pageMake] : []);
                return;
            }
            if (active) {
                // Même chemin que la croix de la pastille « Marque » : retire aussi modèles, options, couleurs
                const pinnedClear = document.querySelector<HTMLInputElement>("#menu-make [data-make-pinned] .RefinementList__clear");
                if (pinnedClear) {
                    pinnedClear.click();
                    return;
                }
                if (!h) return;
                h.clearRefinements("make");
                options.makeDependentFacets.forEach((facet) => h.clearRefinements(facet));
                h.setPage(0).search();
                return;
            }
            if (options.leaveForMarketplaceToggling) {
                options.leaveForMarketplaceToggling("make", value, pageMakes.length > 0 ? pageMakes : pageMake ? [pageMake] : []);
                return;
            }
            const input = document.querySelector<HTMLInputElement>(
                `#menu-make input.RefinementList__checkbox[value="${CSS.escape(value)}"]`,
            );
            if (input) {
                input.click();
                return;
            }
            if (h) h.setPage(0).toggleFacetRefinement("make", value).search();
            return;
        }

        const attribute = kind === "model" ? "model" : "energy_type";
        if (options.leaveForMarketplaceToggling) {
            options.leaveForMarketplaceToggling(attribute, value);
            return;
        }
        if (!h) return;
        // Pastille modèle = une famille : on (dé)choisit toutes ses désignations
        const values = kind === "model"
            ? familyToggleValues(
                  value,
                  refinementsOf(h, "model"),
                  (facetCounts(search.mainIndex.getResults() as ResultsLike | null, "model") ?? []).map((facet) => facet.value),
              )
            : [value];
        values.forEach((item) => h.toggleFacetRefinement(attribute, item));
        h.setPage(0).search();
    });
}
